import React from 'react';
import { Loader2, Receipt } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { ListeEmailsModule } from '../ui/LigneNotification';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';

// Jetons réellement disponibles dans les emails de facture (cf. génération du corps côté serveur) —
// jamais la liste générique ALL_NOTIFICATION_TOKENS, qui contient des variables sans rapport.
const FACTURE_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{entreprise_telephone}}', label: 'Numéro entreprise' },
    { token: '{{entreprise_email}}', label: 'Email entreprise' },
    { token: '{{entreprise_adresse}}', label: 'Adresse entreprise' },
    { token: '{{entreprise_tva}}', label: 'TVA entreprise' },
    { token: '{{entreprise_siret}}', label: 'SIRET entreprise' },
    { token: '{{client_nom}}', label: 'Nom client' },
    { token: '{{date_facture}}', label: 'Date de la facture' },
    { token: '{{montant}}', label: 'Montant total TTC' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer (0 si soldée)' },
    { token: '{{lien_paiement}}', label: 'Lien de paiement Stripe (si configuré)' },
    { token: '{{logo}}', label: 'Votre logo' },
];

/**
 * Réglages des emails du module Facturation, ouverts directement depuis le module (bouton
 * « Paramètres ») — même principe que SubscriptionEmailSettingsModal.tsx et DevisEmailSettingsModal.tsx :
 * chaque module porte ses propres réglages d'email au lieu d'un renvoi vers Paramètres > Notifications.
 * Un seul onglet (Client) : ces deux emails n'ont pas de pendant côté établissement.
 */
export function FactureEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    const [notifySend, setNotifySend] = React.useState(true);
    const [sendSubject, setSendSubject] = React.useState('');
    const [sendBody, setSendBody] = React.useState('');
    const [sendActiveField, setSendActiveField] = React.useState<'subject' | 'body'>('body');
    const sendSubjectRef = React.useRef<HTMLInputElement>(null);
    const sendBodyRef = React.useRef<HTMLTextAreaElement>(null);

    const [sendPaidSubject, setSendPaidSubject] = React.useState('');
    const [sendPaidBody, setSendPaidBody] = React.useState('');
    // La facture DÉJÀ SOLDÉE est un modèle distinct, mais elle partageait l'interrupteur de la facture
    // avec solde restant — donc elle n'en avait aucun. Elle a le sien depuis le 2026-09-18.
    const [notifySendPaid, setNotifySendPaid] = React.useState(true);
    const [sendPaidActiveField, setSendPaidActiveField] = React.useState<'subject' | 'body'>('body');
    const sendPaidSubjectRef = React.useRef<HTMLInputElement>(null);
    const sendPaidBodyRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        moduleApi.getClientNotificationSettings()
            .then((s: any) => {
                setNotifySend(s ? !!s.notify_facture_send : true);
                setNotifySendPaid(s ? s.notify_facture_send_paid !== false : true);
                setSendSubject(s?.facture_send_subject || '');
                setSendBody(s?.facture_send_body || '');
                setSendPaidSubject(s?.facture_send_paid_subject || '');
                setSendPaidBody(s?.facture_send_paid_body || '');
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const insertSendToken = (token: string) => {
        if (sendActiveField === 'subject') insertNotificationToken(sendSubjectRef.current, sendSubject, setSendSubject, token);
        else insertNotificationToken(sendBodyRef.current, sendBody, setSendBody, token);
    };
    const insertSendPaidToken = (token: string) => {
        if (sendPaidActiveField === 'subject') insertNotificationToken(sendPaidSubjectRef.current, sendPaidSubject, setSendPaidSubject, token);
        else insertNotificationToken(sendPaidBodyRef.current, sendPaidBody, setSendPaidBody, token);
    };

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const res: any = await moduleApi.patchClientNotificationSettings('facture', {
                notify_facture_send: notifySend, facture_send_subject: sendSubject, facture_send_body: sendBody,
                notify_facture_send_paid: notifySendPaid, facture_send_paid_subject: sendPaidSubject, facture_send_paid_body: sendPaidBody,
            });
            if (res?.error) { setErreur(res.error); return; }
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    return (
        <Modal
            open
            onClose={onClose}
            size="lg"
            icon={<Receipt size={16} />}
            title="Paramètres des emails de facturation"
            subtitle="Un clic sur un titre ouvre son texte et l'aperçu ; l'interrupteur active ou coupe l'email."
            footer={<button type="button" onClick={onClose} className="h-9 px-4 inline-flex items-center rounded-full border border-border bg-surface text-[13px] font-semibold text-content hover:bg-surface-soft whitespace-nowrap shrink-0 transition-colors">Fermer</button>}
        >
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : (
                <div className="space-y-3">
                    <ListeEmailsModule enregistrer={enregistrer} etat={saving ? 'en_cours' : erreur ? 'echec' : saved ? 'fait' : 'repos'}>
                    <NotificationBlock title="Facture avec solde restant" description="Part quand vous cliquez sur « Envoyer la facture » et qu'il reste un montant à payer (acompte ou rien réglé)."
                        toggle={{ checked: notifySend, onChange: setNotifySend }}
                        subject={sendSubject} setSubject={setSendSubject} body={sendBody} setBody={setSendBody}
                        subjectRef={sendSubjectRef} bodyRef={sendBodyRef}
                        activeField={sendActiveField} setActiveField={setSendActiveField}
                        onInsertToken={insertSendToken} onWrap={(m) => wrapNotificationSelection(sendBodyRef.current, sendBody, setSendBody, m)} tokens={FACTURE_TOKENS} />
                    <NotificationBlock title="Facture déjà soldée" description="Part quand vous cliquez sur « Envoyer la facture » et qu'elle est déjà réglée en intégralité."
                        toggle={{ checked: notifySendPaid, onChange: setNotifySendPaid }}
                        subject={sendPaidSubject} setSubject={setSendPaidSubject} body={sendPaidBody} setBody={setSendPaidBody}
                        subjectRef={sendPaidSubjectRef} bodyRef={sendPaidBodyRef}
                        activeField={sendPaidActiveField} setActiveField={setSendPaidActiveField}
                        onInsertToken={insertSendPaidToken} onWrap={(m) => wrapNotificationSelection(sendPaidBodyRef.current, sendPaidBody, setSendPaidBody, m)} tokens={FACTURE_TOKENS} />
                    </ListeEmailsModule>
                    {erreur && (
                        <p className="text-[12px] font-semibold" style={{ color: 'var(--st-danger-fg)' }}>{erreur}</p>
                    )}
                </div>
            )}
        </Modal>
    );
}
